import React, { useState, useEffect } from 'react';
import { Bell, X, Check, XCircle, Users, Trash2 } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';

const Notifications = ({ user, isOpen, onClose, onNotificationCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
    }
  }, [isOpen, user]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/notifications?user_id=${user.id}`);
      setNotifications(response.data);
      
      // Update unread count
      const unreadCount = response.data.filter(n => !n.read).length;
      onNotificationCountChange && onNotificationCountChange(unreadCount);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/accept-invite?user_id=${user.id}`);
      fetchNotifications();
    } catch (err) {
      console.error('Error accepting invite:', err);
      alert(err.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvite = async (notificationId) => {
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/decline-invite?user_id=${user.id}`);
      fetchNotifications();
    } catch (err) {
      console.error('Error declining invite:', err);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read?user_id=${user.id}`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all?user_id=${user.id}`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${notificationId}?user_id=${user.id}`);
      fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'club_invite':
      case 'club_added':
        return <Users className="h-5 w-5 text-purple-500" />;
      default:
        return <Bell className="h-5 w-5 text-blue-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md max-h-[80vh] overflow-hidden p-0 [&>button]:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-bold">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            {notifications.some(n => !n.read) && (
              <Button
                onClick={handleMarkAllRead}
                variant="ghost"
                size="sm"
                className="text-xs text-gray-400 hover:text-white"
              >
                Mark all read
              </Button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-red-600 border-t-transparent rounded-full"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-zinc-800/50 transition-colors ${
                    !notification.read ? 'bg-zinc-800/30' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {notification.data?.club_avatar ? (
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-700">
                          <img
                            src={notification.data.club_avatar}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{notification.title}</p>
                      <p className="text-sm text-gray-400 mt-0.5">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatTime(notification.created_at)}</p>

                      {/* Actions for club invite */}
                      {notification.type === 'club_invite' && !notification.read && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => handleAcceptInvite(notification.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => handleDeclineInvite(notification.id)}
                            size="sm"
                            variant="outline"
                            className="border-zinc-600"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex items-start gap-1">
                      {!notification.read && notification.type !== 'club_invite' && (
                        <button
                          onClick={() => handleMarkRead(notification.id)}
                          className="p-1 text-gray-400 hover:text-white"
                          title="Mark as read"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Notification Bell Component (for header)
export const NotificationBell = ({ user, onClick, count }) => {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-full hover:bg-zinc-700 text-gray-400 hover:text-white transition-colors"
      title="Notifications"
    >
      <Bell className="h-5 w-5" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
};

export default Notifications;
